import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Gauge, Play, ShieldCheck, PauseCircle, TrendingDown, CalendarClock } from 'lucide-react';
import { useAuth, useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', amber: '#8A6D1D', teal: '#0E7C7B', line: '#E4E9EF' };
const ORDER = ['interpreter', 'verifier', 'researcher', 'dean', 'evaluator', 'supervisor', 'report_reader', 'communicator', 'assembler', 'critic', 'coverage_analyst', 'case_planner'];
const STATUS_STYLE = (s) => (s?.startsWith('hold') || s === 'not_built')
  ? { bg: '#EEF1F5', fg: C.slate }
  : s === 'partial' ? { bg: '#FDF6E3', fg: C.amber } : { bg: '#E8F4EC', fg: C.green };

function ScoreBadge({ row }) {
  if (row.score === null || row.score === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded" style={{ background: '#EEF1F5', color: C.slate }}>
        <PauseCircle size={11} /> NO SCORE — NOT BUILT
      </span>
    );
  }
  const color = row.score >= 90 ? C.green : row.score >= 70 ? C.amber : '#B0413E';
  return <span className="text-lg font-bold font-mono" style={{ color }}>{row.score.toFixed(1)}</span>;
}

export default function ArchetypeScoreboardPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [board, setBoard] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/agent-governance/scoreboard'); setBoard(r.data); } catch { /* noop */ }
  }, [api]);
  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);

  const runAll = async () => {
    setRunning(true);
    toast.info('Evaluating every agent type through its scaffold — live LLM calls, allow a minute or two…');
    try {
      const r = await api.post('/agent-governance/evaluate-archetypes', {}, { timeout: 300000 });
      setBoard(r.data.scoreboard);
      const scored = Object.entries(r.data.results).filter(([, v]) => v.score !== null);
      toast.success(`Evaluation complete — ${scored.length} agent types scored, ${scored.filter(([, v]) => v.promoted).length} at/above champion`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Evaluation failed'); }
    setRunning(false);
  };

  if (!isAdmin) {
    return <div className="p-6 text-sm" style={{ color: C.slate }} data-testid="scoreboard-restricted">The agent-type scoreboard is owner/admin only.</div>;
  }
  if (!board) return <div className="p-6 text-xs" style={{ color: C.slate }}>Loading scoreboard…</div>;

  const rows = ORDER.filter(n => board.archetypes[n]).map(n => ({ name: n, ...board.archetypes[n] }));
  const we = board.weekly_eval;

  return (
    <div className="space-y-4" data-testid="archetype-scoreboard-page">
      {/* Weekly auto-eval status */}
      {we && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs" data-testid="weekly-eval-banner"
          style={we.drops.length > 0
            ? { background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.25)', color: '#B0413E' }
            : { background: 'rgba(14,124,123,0.05)', border: '1px solid rgba(14,124,123,0.2)', color: C.teal }}>
          {we.drops.length > 0 ? <TrendingDown size={14} className="shrink-0" /> : <CalendarClock size={14} className="shrink-0" />}
          <span style={{ color: C.navy }}>
            <b>Weekly auto-eval</b> ({we.trigger}) last ran {we.at.slice(0, 16).replace('T', ' ')} UTC —{' '}
            {we.drops.length > 0
              ? <b style={{ color: '#B0413E' }}>score drop on {we.drops.join(', ')} — run below standing champion, gate held it</b>
              : 'no score drops; every scored agent type at or above champion'}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="text-xs max-w-3xl" style={{ color: C.slate }}>
          <Gauge size={13} className="inline mr-1" style={{ color: C.teal }} />
          Every agent type (twelve, counted by the registry) is scored the same way — golden cases through <code>scaffold.invoke()</code>, champion-gated.
          Types not yet built carry <b>no score</b>: no agent quality claim without a golden-set score and its basis.
        </div>
        <button onClick={runAll} disabled={running}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-50 shrink-0"
          style={{ background: C.navy }} data-testid="run-archetype-evals-btn">
          <Play size={12} /> {running ? 'Evaluating…' : 'Run evaluation'}
        </button>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: C.line }}>
              <th className="px-4 py-2.5">Agent type</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Score</th>
              <th className="px-3 py-2.5">Basis</th>
              <th className="px-3 py-2.5">Last eval</th>
              <th className="px-3 py-2.5">Gate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const ss = STATUS_STYLE(r.status);
              return (
                <tr key={r.name} className="border-b last:border-b-0 align-top" style={{ borderColor: C.line }} data-testid={`scoreboard-row-${r.name}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold capitalize" style={{ color: C.navy }}>{r.name.replace(/_/g, ' ')}</div>
                    {r.version && <div className="text-[10px] font-mono" style={{ color: C.slate }}>{r.version}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase whitespace-nowrap" style={{ background: ss.bg, color: ss.fg }}>{r.status}</span>
                  </td>
                  <td className="px-3 py-3" data-testid={`scoreboard-score-${r.name}`}>
                    <ScoreBadge row={r} />
                    {r.weekly?.drop && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase"
                        style={{ background: 'rgba(226,75,74,0.1)', color: '#B0413E' }} data-testid={`score-drop-${r.name}`}>
                        <TrendingDown size={10} /> weekly drop {r.weekly.delta}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[11px] max-w-sm pr-6" style={{ color: C.slate }}>{r.basis}</td>
                  <td className="px-3 py-3 text-[11px] whitespace-nowrap" style={{ color: C.slate }}>{r.last_eval_at ? r.last_eval_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                  <td className="px-3 py-3">
                    {r.last_gate ? (
                      <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: r.last_gate.promoted ? C.green : '#B0413E' }}>
                        <ShieldCheck size={11} /> {r.last_gate.promoted ? 'promoted' : 'held below champion'}
                        {r.last_gate.champion_score !== null && r.last_gate.champion_score !== undefined && ` (champ ${r.last_gate.champion_score})`}
                      </span>
                    ) : <span className="text-[11px]" style={{ color: C.slate }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px]" style={{ color: C.slate }} data-testid="scoreboard-note">{board.note}</div>
    </div>
  );
}
